import * as React from 'react';
import { memo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { routes } from 'defs';

import { warn } from 'helper/debug';
import i18n from 'helper/i18n';
import { compileDynamicRoute } from 'helper/string';

import Flag from 'components/atoms/Flag';
import Image from 'components/atoms/Image';

import { FavoriteType } from 'types/Favorites';

export type FavoriteTeaserProps = FavoriteType & {
	externalId: string;
	frontendContentType?: string;
};

const slugLabel = (t: string): string => {
	if (!t) {
		warn(`Could not fetch slug to favorite for contentType ${t}`);
		return '';
	}

	return i18n(`react.impulsCoach.routes.favorites.detail.${t.toLocaleLowerCase().replace(/-/gi, '')}.href`);
};

const generateFavoriteDetailUrl = (contentType: string, id: string): string => {
	if (!contentType || !id) {
		warn(`Could not generate url to favorite for contentType ${contentType} and id ${id}`);
		return '';
	}

	return compileDynamicRoute(routes.favorites.detail.href, {
		type: slugLabel(contentType),
		id,
	});
};

const getModifierByContent = (type: string): string => {
	switch (type) {
		case 'exercise':
			return 'task';
		default:
			return type;
	}
};

export const FavoriteTeaser = ({ title, image, contentType, externalId, frontendContentType }: FavoriteTeaserProps) => {
	return (
		<div className="m-favorite-teaser">
			<RouterLink className="m-favorite-teaser__link" to={generateFavoriteDetailUrl(contentType, externalId)}>
				<div className="m-favorite-teaser__box">
					<div className="m-favorite-teaser__box-wrap">
						<div className="m-favorite-teaser__box-triangle" />
						<div className="m-favorite-teaser__box-text">
							{i18n('react.impulsCoach.components.favoriteTeaser.moreDetails')}
						</div>
					</div>
					{image && (
						<div className="m-favorite-teaser__image">
							<Image {...image} />
						</div>
					)}
				</div>
				<div className="m-favorite-teaser__content">
					{frontendContentType && (
						<Flag
							modifier={['rect', getModifierByContent(frontendContentType.toLowerCase())]}
							text={i18n(
								`react.impulsCoach.components.favorites.flag.${frontendContentType.toLowerCase()}`
							)}
						/>
					)}
					<h2 className="m-favorite-teaser__title">{title}</h2>
				</div>
			</RouterLink>
		</div>
	);
};

FavoriteTeaser.displayName = 'FavoriteTeaser';

export default memo(FavoriteTeaser);
